<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-20">
    <FormSelect multiple class="w-full mt-3" v-model="features" :options="ai_features" label="Model Features" @update:modelValue="onAIFeaturesChange" />
    <div class="flex flex-row mt-3">
      <FormSelect
        :label="$t('evaluate.provider')"
        v-model="aiPayload.provider"
        :options="providers"
        :selectable="(option) => !option.disabled"
        :no-options="providersNoOptionsText"
        :reduce="(option) => option.value"
        class="w-40 flex-shrink-0"
        @update:modelValue="onProviderChange"
      />
      <FormSelect :label="$t('evaluate.model')" v-model="aiPayload.model" :options="models" :reduce="(option) => option.value" class="w-full ml-5" @update:modelValue="emitPayload" />
    </div>
    <FormSelect
      :label="`${$t('evaluate.persona')} (Optional)`"
      v-model="aiPayload.persona"
      :options="personas"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      show-add-button
      clearable
      @add="onAddPersonaClick"
      @update:modelValue="emitPayload"
    />
    <PhraseEditor v-model="aiPayload.context" multiline :items="variables" class="w-full mt-3" :label="$t('evaluate.context')" @update:modelValue="emitPayload" />
    <VariableSelect v-if="isFileApi" label="Select file to upload" v-model="aiPayload.file" class="mt-3 w-full" :filter-data-type="['PDF', 'Image']" @update:modelValue="emitPayload" />
    <FormSelect
      :label="$t('evaluate.reasoning_effort')"
      v-model="aiPayload.reasoning_effort"
      v-if="isReasoning"
      :options="[
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
      ]"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      @update:modelValue="emitPayload"
    />
    <FormSelect
      :label="$t('evaluate.search_effort')"
      v-model="aiPayload.search_effort"
      v-if="isSearchEffort"
      :options="[
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
      ]"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      @update:modelValue="emitPayload"
    />
    <div class="flex flex-row" v-if="this.aiPayload.model">
      <div class="flex flex-col mt-3 pb-5">
        <FormLabel class="mt-3" :label="$t('evaluate.temperature')" />
        <div class="flex">
          <input type="range" v-model="aiPayload.temperature" min="0" max="2" step="0.1" class="max-w-200" @input="emitPayload" />
          <input type="number" v-model="aiPayload.temperature" min="0" max="2" step="0.1" class="w-12 ml-2 bg-white border-stroke border-solid border rounded-4 px-1" @input="emitPayload" />
        </div>
      </div>

      <div class="flex flex-col mt-3 ml-10">
        <FormLabel class="mt-3" :label="$t('evaluate.max_tokens')" />
        <div class="flex">
          <input type="range" v-model="aiPayload.max_tokens" min="100" :max="model.max_tokens" step="100" class="max-w-300" @input="emitPayload" />
          <input
            type="number"
            v-model="aiPayload.max_tokens"
            min="100"
            :max="model.max_tokens"
            step="100"
            class="w-30 ml-2 bg-white border-stroke border-solid border rounded-4 px-1"
            @input="emitPayload"
          />
        </div>
      </div>
    </div>
    <div class="mt-6">
      <div class="flex flex-row">
        <div class="flex text-gray-600 font-500 text-13 items-center">Output</div>
        <div class="ml-8 flex gap-x-6 text-13 text-gray-600 items-center" v-if="isStructuredOutput">
          <RadioButton name="output_type" v-model="aiPayload.output_type" value="simple" label="Simple" @update:modelValue="onOutputTypeChange" />
          <RadioButton name="output_type" v-model="aiPayload.output_type" value="structured" label="Structured" @update:modelValue="onOutputTypeChange" />
        </div>
        <div class="flex flex-row ml-auto justify-end w-200 items-center">
          <div class="text-gray-600 text-13">Cache:</div>
          <FormSelect v-model="aiPayload.cache" :options="cacheOptions" :reduce="(option) => option.value" class="w-40 ml-3" @update:modelValue="emitPayload" />
        </div>
      </div>
      <hr class="mt-2" />

      <FormSelect
        :label="$t('evaluate.replace_citations')"
        v-model="aiPayload.replace_citations"
        v-if="isDeepResearch || isSearchEffort"
        :options="[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ]"
        :reduce="(option) => option.value"
        class="w-full mt-3"
        @update:modelValue="emitPayload"
      />

      <div class="mt-3" v-if="isSimpleOutput">
        <div class="flex justify-between mb-1">
          <FormLabel label="Assign Response to Variable(s)" />
          <IconButton icon="plus" size="4" dense @click="addResponseParameter" />
        </div>
        <div v-for="(parameter, index) in responseParameters" :key="index" class="flex gap-x-2 mb-1 items-center">
          <FormInput v-model="parameter.key" class="w-full" placeholder="Enter object path (@ for root or use dot notation)" @update:modelValue="emitPayload" />
          <Icon name="east" class="w-4 h-4 flex-shrink-0 text-gray-600" />
          <VariableSelect :system="false" :filterDataType="['DataTable', 'Text', 'YesNo', 'Number']" v-model="parameter.value" class="w-300 flex-shrink-0" @update:modelValue="emitPayload" />
          <IconButton :disabled="Object.keys(responseParameters)?.length === 1 && index === 0" icon="delete" class="text-primary" dense size="4" @click="onDeleteResponseClick(index)" />
        </div>
      </div>
      <div class="mt-3" v-else>
        <div class="flex mb-1">
          <FormLabel class="pt-2 mr-3" label="Assign Structured AI Output to Variable(s)" />
          <IconButton icon="plus" size="4" dense @click="addStructuredOutputParameter" />
        </div>
        <div v-for="(parameter, index) in aiPayload.structuredOutput" :key="index" class="flex flex-col max-w-700 gap-x-2 mb-1 items-center">
          <div class="flex flex-row w-full">
            <FormInput label="Parameter" v-model="parameter.parameter" class="w-150 flex-shrink-0" placeholder="Enter parameter name" @update:modelValue="emitPayload" />
            <FormInput label="Description (optional)" v-model="parameter.description" class="w-250 flex-shrink-0 ml-1" placeholder="Enter description" @update:modelValue="emitPayload" />
            <FormSelect
              label="Data Type"
              v-model="parameter.type"
              :options="structuredOutputTypeOptions"
              class="w-160 flex-shrink-0 ml-1"
              @update:modelValue="structuredOutputDataTypeChanged(index)"
            />
            <Icon name="east" class="w-4 h-4 flex-shrink-0 mt-8 text-gray-600" />
            <VariableSelect
              label="Assign to"
              :system="false"
              :filterDataType="filterStructuredOutputVariableType(parameter?.type?.value)"
              v-model="parameter.variable"
              class="w-200 flex-shrink-0 flex-grow-0"
              @update:modelValue="emitPayload"
            />
            <IconButton :disabled="Object.keys(responseParameters)?.length === 1 && index === 0" icon="delete" class="text-primary mt-5" dense size="4" @click="onDeleteStructuredOutputClick(index)" />
          </div>
          <div class="w-full flex flex-col ml-10 mt-3 mb-3" v-if="parameter?.type?.value === 'object_array'">
            <div class="flex flex-row">
              <FormLabel class="pt-1 mr-3" label="Nested Parameters" />
              <IconButton icon="plus" size="4" dense @click="addStructuredOutputNestedParameter(parameter)" />
            </div>
            <div class="flex flex-row" v-for="(nested_parameter, i) in parameter.nested_parameters" :key="i">
              <FormInput label="Parameter" v-model="nested_parameter.parameter" class="w-150 flex-shrink-0" placeholder="Enter parameter name" @update:modelValue="emitPayload" />
              <FormInput label="Description (optional)" v-model="nested_parameter.description" class="w-250 flex-shrink-0 ml-1" placeholder="Enter description" @update:modelValue="emitPayload" />
              <FormSelect
                label="Data Type"
                v-model="nested_parameter.type"
                :options="structuredOutputTypeSimpleOptions"
                class="w-160 flex-shrink-0 ml-1"
                @update:modelValue="structuredOutputDataTypeChanged(index)"
              />
              <IconButton icon="delete" class="text-primary mt-5" dense size="4" @click="onDeleteStructuredOutputNestedParameterClick(parameter.nested_parameters, i)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { AI_FEATURES, AI_FEATURES_SELECTION } from '../../../constants';
import CreatePersonaModal from '../../project/CreatePersonaModal.vue';
import EvaluateItemsConfig from './EvaluateItemsConfig';
import useAIModelSource from './useAIModelSource';
import debounce from '../../../helpers/debounce';

export default {
  name: 'AITextGenerationConfig',
  mixins: [useAIModelSource],
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      aiPayload: structuredClone(EvaluateItemsConfig['ai-text-gen'].payload),
      AI_FEATURES,
      features: [],
      cacheOptions: [
        { label: 'No Caching', value: 'no' },
        { label: '10 Minutes', value: '10m' },
        { label: '30 Minutes', value: '30m' },
        { label: '1 Hour', value: '1h' },
        { label: '2 Hours', value: '2h' },
        { label: '6 Hours', value: '6h' },
        { label: 'Until midnight', value: 'midnight' },
      ],
      structuredOutputTypeOptions: [
        { label: 'Integer', value: 'integer' },
        { label: 'Integer List (array)', value: 'array' },
        { label: 'Number', value: 'number' },
        { label: 'Number List (array)', value: 'array' },
        { label: 'Text (string)', value: 'string' },
        { label: 'Text List (array)', value: 'array' },
        { label: 'YesNo (boolean)', value: 'boolean' },
        { label: 'Object List', value: 'object_array' },
        // { label: 'Predefined (enum)', value: 'enum' },
      ],
      structuredOutputTypeSimpleOptions: [
        { label: 'Integer', value: 'integer' },
        { label: 'Number', value: 'number' },
        { label: 'Text (string)', value: 'string' },
        { label: 'YesNo (boolean)', value: 'boolean' },
      ],
      responseParameters: [],
    };
  },
  computed: {
    ...mapState(['availableAIProviders']),
    ...mapGetters(['getProjectAIProviderOptions', 'getProjectAIProviders', 'getProjectPersonas', 'getProjectVariables']),
    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: this.aiPayload,
      };
    },
    ai_features() {
      return Object.keys(AI_FEATURES_SELECTION)
        .filter((f) => !this.features.map((item) => item.value).includes(f))
        .map((key) => ({ label: AI_FEATURES_SELECTION[key], value: AI_FEATURES_SELECTION[key] }));
    },
    providers() {
      if (this.features.length) {
        const requiredFeatures = this.features.map((item) => item.label);
        return this.getProjectAIProviderOptions().filter((option) => this.providerHasModelWithFeatures(option.value, requiredFeatures));
      }
      return [];
    },
    providersNoOptionsText() {
      if (this.features.length === 0) {
        return 'Please select a model feature';
      }
      if (Object.keys(this.availableAIProviders).length === 0) {
        return 'No AI Providers enabled';
      }
      return 'No AI Providers provide selected features combined in a model';
    },
    personas() {
      return Object.values(this.getProjectPersonas()).map((item) => ({ label: item.name, value: item.persona_id }));
    },
    /**
     * Models of the selected provider that support every selected model feature.
     * @returns {Array<{label: string, value: string}>} Model select options.
     */
    models() {
      if (!this.aiPayload.provider) return [];
      const requiredFeatures = this.features.map((item) => item.label);
      return this.modelsForProvider(this.aiPayload.provider)
        .filter((m) => requiredFeatures.every((f) => (m.features || []).includes(f)))
        .map((item) => ({ label: `<b>${item.name}</b> - <i class="text-11">Cost: ${item.cost}</i>`, value: item.model }));
    },
    projectId() {
      return this.$route.params.projectId;
    },
    variables() {
      return Object.keys(this.getProjectVariables()).map((i) => ({ label: i, value: i }));
    },
    model() {
      let model = { max_tokens: '1000' };
      if (this.aiPayload.model) {
        model = this.modelsForProvider(this.aiPayload.provider).find((m) => m.model === this.aiPayload.model) || model;
      }
      return model;
    },
    isDeepResearch() {
      return this.features.map((item) => item.label).includes(AI_FEATURES.DEEP_RESEARCH);
    },
    isFileApi() {
      return this.features.map((item) => item.label).includes(AI_FEATURES.FILE_API);
    },
    isReasoning() {
      return this.features.map((item) => item.label).includes(AI_FEATURES.REASONING);
    },
    isSearchEffort() {
      return this.model?.features?.includes(AI_FEATURES.SEARCH_EFFORT) || false;
    },
    isStructuredOutput() {
      return this.features.map((item) => item.label).includes(AI_FEATURES.STRUCTURED_OUTPUT);
    },
    isSimpleOutput() {
      return this.aiPayload.output_type === 'simple';
    },
  },
  methods: {
    filterStructuredOutputVariableType(type) {
      switch (type) {
        case 'string':
        case 'enum':
          return ['Text'];
        case 'number':
        case 'integer':
          return ['Number'];
        case 'boolean':
          return 'YesNo';
        case 'array':
        case 'object':
        case 'object_array':
          return ['DataTable'];
        default:
          return ['DataTable', 'Text', 'YesNo', 'Number'];
      }
    },
    structuredOutputDataTypeChanged(index) {
      this.aiPayload.structuredOutput[index].variable = '';
      this.$emit('update', this.payload);
    },
    // eslint-disable-next-line
    emitPayload: debounce(function () {
      if (this.aiPayload.max_tokens > this.model.max_tokens) {
        this.aiPayload.max_tokens = this.model.max_tokens;
      } else {
        this.aiPayload.max_tokens = this.aiPayload.max_tokens.toString();
      }
      this.aiPayload.response = this.convertArrayToObject(this.responseParameters);
      this.aiPayload.features = this.features.map((f) => f.value);
      // Snapshot the selected model's config so the runtime (esp. LM Studio) can
      // shape requests by context length / token param / responses support.
      this.aiPayload.model_config = this.aiPayload.model ? this.buildDynamicAwareModelConfig(this.aiPayload.provider, this.aiPayload.model) : null;
      this.$emit('update', this.payload);
    }, 300),
    onProviderChange() {
      this.aiPayload.model = '';
      this.aiPayload.model_config = null;
      this.aiPayload.max_tokens = '1000';
      this.$emit('update', this.payload);
    },
    onAddPersonaClick() {
      this.$showModal(CreatePersonaModal, { projectId: this.projectId });
    },
    onAIFeaturesChange() {
      this.aiPayload.provider = '';
      this.aiPayload.model = '';
      this.aiPayload.model_config = null;
      this.aiPayload.max_tokens = '1000';
      if (!this.isStructuredOutput && !this.isSimpleOutput) {
        this.aiPayload.output_type = 'simple';
      }
      this.$emit('update', this.payload);
    },
    onOutputTypeChange() {
      if (this.aiPayload.output_type === 'simple' && !this.responseParameters.length) this.addResponseParameter();
      if (this.aiPayload.output_type === 'structured' && !this.aiPayload.structuredOutput.length) this.addStructuredOutputParameter();
      this.$emit('update', this.payload);
    },
    convertArrayToObject(arr) {
      return arr
        .filter((header) => header.key)
        .reduce((acc, val) => {
          acc[val.key] = val.value;
          return acc;
        }, {});
    },
    convertObjectToArray(obj) {
      return Object.keys(obj).map((headerKey) => ({ key: headerKey, value: obj[headerKey] }));
    },
    addResponseParameter() {
      this.responseParameters.push({ key: '', value: '' });
    },
    addStructuredOutputParameter() {
      this.aiPayload.structuredOutput.push({ parameter: '', description: '', variable: '', type: '', nested_parameters: [], enum: [], required: true });
    },
    addStructuredOutputNestedParameter(parameter) {
      if (!Array.isArray(parameter?.nested_parameters)) {
        parameter.nested_parameters = [];
      }
      parameter.nested_parameters.push({ parameter: '', description: '', type: '', required: true });
    },
    onDeleteStructuredOutputNestedParameterClick(nested_parameters, index) {
      nested_parameters.splice(index, 1);
      this.emitPayload();
    },
    onDeleteResponseClick(index) {
      this.responseParameters.splice(index, 1);
      this.emitPayload();
    },
    onDeleteStructuredOutputClick(index) {
      this.aiPayload.structuredOutput.splice(index, 1);
      this.emitPayload();
    },
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          this.aiPayload = JSON.parse(JSON.stringify(this.item.payload));
          this.responseParameters = this.convertObjectToArray(this.aiPayload.response);
          this.features = this.aiPayload.features.map((f) => ({ label: f, value: f }));
          if (this.aiPayload.output_type === 'simple' && !this.responseParameters.length) this.addResponseParameter();
          if (this.aiPayload.output_type === 'structured' && !this.aiPayload.structuredOutput.length) this.addStructuredOutputParameter();
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
